import { useState, useEffect } from 'react';
import { Spinner } from '../components/Spinner';

export default function WalterPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(0);

  const fetchData = async () => {
    try {
      const res = await fetch('/api/walter/runs');
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
      setLastRefreshed(0);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setLastRefreshed(s => s + 1), 1000);
    return () => clearInterval(tick);
  }, []);

  const runsLast3h = data?.runs?.filter(r => {
    const age = (Date.now() - new Date(r.time).getTime()) / 60000;
    return age <= 180;
  }).length ?? 0;

  const statusFor = (gap) => {
    if (gap === null) return null;
    if (gap <= 10) return { label: 'ok', color: 'text-green-400' };
    if (gap <= 15) return { label: 'slow', color: 'text-orange-400' };
    return { label: 'gap', color: 'text-red-400' };
  };

  return (
    <div className="">
      <div className="flex items-center justify-end mb-6">
        <span className="text-sm text-gray-500">
          Last refreshed: {lastRefreshed}s ago
        </span>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 mb-6 text-red-200">
          {error}
        </div>
      )}

      {data?.summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Last Run</div>
            <div className="text-xl font-semibold text-white">
              {data.summary.last_crawl_at
                ? new Date(data.summary.last_crawl_at).toLocaleTimeString()
                : '—'}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {data.summary.age_minutes != null ? `${data.summary.age_minutes} min ago` : ''}
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Avg Gap</div>
            <div className="text-xl font-semibold text-white">
              {data.summary.avg_gap != null ? `${data.summary.avg_gap} min` : '—'}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {data.summary.min_gap != null ? `min ${data.summary.min_gap} / max ${data.summary.max_gap}` : ''}
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Runs (last 3h)</div>
            <div className="text-xl font-semibold text-white">{runsLast3h}</div>
            <div className="text-sm text-gray-500 mt-1">{data.summary.total_runs} total</div>
          </div>
        </div>
      )}

      {data?.runs && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {/* Mobile: card list */}
          <div className="block sm:hidden divide-y divide-gray-100">
            {data.runs.map((run, i) => {
              const status = statusFor(run.gap_minutes);
              return (
                <div key={i} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <span className="font-mono text-white text-sm">
                      {new Date(run.time).toLocaleTimeString()}
                    </span>
                    <span className="ml-3 font-mono text-gray-500 text-sm">
                      {run.gap_minutes != null ? `+${run.gap_minutes}m` : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500 text-xs">{run.size_kb}kb</span>
                    {status && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 ${status.color}`}>
                        {status.label}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop: table */}
          <table className="hidden sm:table w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-gray-500">
                <th className="px-4 py-3 text-left font-medium">#</th>
                <th className="px-4 py-3 text-left font-medium">Time</th>
                <th className="px-4 py-3 text-left font-medium">Gap</th>
                <th className="px-4 py-3 text-left font-medium">Size</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((run, i) => {
                const status = statusFor(run.gap_minutes);
                return (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                    <td className="px-4 py-2 font-mono">{new Date(run.time).toLocaleTimeString()}</td>
                    <td className="px-4 py-2 font-mono text-gray-400">
                      {run.gap_minutes != null ? `+${run.gap_minutes}m` : ''}
                    </td>
                    <td className="px-4 py-2 text-gray-400">{run.size_kb}kb</td>
                    <td className="px-4 py-2">
                      {status && (
                        <span className={`font-medium ${status.color}`}>{status.label}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!data && !error && (
        <div className="flex items-center justify-center py-16">
          <Spinner size={40} />
        </div>
      )}
    </div>
  );
}
